import React, { useCallback, useMemo } from 'react';

import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { Stack } from '@mui/material';

import { ReopenManualRoomDate } from '~/types/reopen';

import { formatDateRange } from '~/utils/dateRangeUtils';

import EditableDateRow from './EditableDateRow';
import ReopenRateRow from './ReopenRateRow';

interface Props {
  date: ReopenManualRoomDate;
  roomIndex: number;
  catalogRoomRates: Array<{ id: string; name: string }>;
  deselectedBlockIds: Set<string>;
  onToggle: (roomIndex: number, dateId: string, selected: boolean) => void;
  onEdit: (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => void;
  onDelete: (roomIndex: number, dateId: string) => void;
  onBlockToggle: (blockId: string, selected: boolean) => void;
}

export default function ReopenManualDateRow({
  date,
  roomIndex,
  catalogRoomRates,
  deselectedBlockIds,
  onToggle,
  onEdit,
  onDelete,
  onBlockToggle,
}: Props) {
  const handleToggle = useCallback((v: boolean) => onToggle(roomIndex, date.id, v), [onToggle, roomIndex, date.id]);
  const handleEdit = useCallback(
    (range: { start: string; end: string }) => onEdit(roomIndex, date.id, range),
    [onEdit, roomIndex, date.id],
  );
  const handleDelete = useCallback(() => onDelete(roomIndex, date.id), [onDelete, roomIndex, date.id]);

  const rateRows = useMemo(() => {
    const rateNameById = new Map(catalogRoomRates.map((r) => [r.id, r.name]));
    return date.impacts.flatMap((impact) =>
      impact.blackout.blocks.map((block) => ({
        blockId: block.id,
        rateName: rateNameById.get(block.room_rate_id) ?? block.room_rate_id,
        impact,
      })),
    );
  }, [date.impacts, catalogRoomRates]);

  return (
    <EditableDateRow
      start={date.start}
      end={date.end}
      selected={date.selected}
      disabled={date.impacts.length === 0}
      onToggle={handleToggle}
      onEdit={handleEdit}
      onDelete={handleDelete}
      icon={<LockOpenOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
      chipSx={{ bgcolor: 'success.50', color: 'success.dark', fontSize: 11 }}
      editLabel={`Edit ${formatDateRange(date.start, date.end)}`}
      deleteLabel={`Delete ${formatDateRange(date.start, date.end)}`}
    >
      {rateRows.length > 0 && date.selected && (
        <Stack gap={0.5} sx={{ pl: 4 }}>
          {rateRows.map((row) => (
            <ReopenRateRow
              key={row.blockId}
              rateName={row.rateName}
              blockId={row.blockId}
              impact={row.impact}
              selected={!deselectedBlockIds.has(row.blockId)}
              onToggle={onBlockToggle}
            />
          ))}
        </Stack>
      )}
    </EditableDateRow>
  );
}
